// utilities/ticketListParams.js
//
// SOUND-693 — the GET /tickets query params, DERIVED from the declared tickets
// filters in config/searchConfig.js instead of a hand-written allowlist.
//
// useTickets used to list every param by hand (`if (filters.x) params.set(...)`)
// AND again in its useCallback deps. A filter declared in the config but not
// listed in both places rendered, wrote to the URL, and was then dropped before
// the request — it did nothing, silently. SOUND-688 fixed the same class in
// useMidApplications; this is the tickets twin, and it landed with the variant
// filter (SOUND-785) because that filter would otherwise have been the next one
// to vanish.
//
// One deliberate difference from SOUND-688: defaults are NOT pruned. `state`
// defaults to "open", and the server treats a MISSING state as "no filter", so
// pruning it would turn the default inbox into every ticket. The rule is the
// one the old guards encoded: send every declared filter that has a value.
//
// `sort` is declared in the config (so it persists in the URL) but rides the
// hook's top-level `sort` argument, which owns the default — so it is excluded
// here, never sent twice.

import { makeSearchHelpers } from "utilities/searchDynamic";

const ticketSearch = makeSearchHelpers("tickets");

/** Declared filter keys that useTickets sends as their own params. */
export const TICKET_LIST_EXCLUDED_KEYS = ["sort"];

function isEmpty(v) {
  return v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0);
}

/**
 * @param {Object} filters - filter state (SearchContext, or a caller's literal)
 * @returns {Object<string, string>} param name → value, empties dropped
 */
export function ticketListParams(filters = {}) {
  const raw = ticketSearch.toApiParams(filters || {}, { exclude: TICKET_LIST_EXCLUDED_KEYS });
  const out = {};
  for (const [k, v] of Object.entries(raw)) {
    if (isEmpty(v)) continue;
    out[k] = Array.isArray(v) ? v.join(",") : String(v);
  }
  return out;
}

/** The declared fields this module sends — exported for the regression test. */
export function ticketListFilterFields() {
  return ticketSearch.fields.filter((f) => !TICKET_LIST_EXCLUDED_KEYS.includes(f.key));
}
